import { Info } from "lucide-react";
import { Field, TextField, TextAreaField, YesNoCards, OptionCards } from "@/components/controls";
import { LOAD_TRANSPORT, PHYSICAL_OPTIONS, DRESS_CODE_OPTIONS, DRESS_CODE_HINT, needsLogistics } from "@/lib/formConfig";

export default function StepRequirements({ data, setField }) {
  const showLogistics = needsLogistics(data.position);

  return (
    <div className="space-y-7">
      <div>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
          Transportation &amp; Job Requirements
        </h2>
        <p className="text-sm sm:text-base text-slate-600 mt-1">A few logistics and role requirements before we wrap up.</p>
      </div>

      {showLogistics ? (
        <div className="space-y-7 rounded-2xl border-2 border-slate-200 bg-slate-50 p-5 sm:p-6">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
            <p className="text-sm text-slate-600 leading-relaxed">
              As a driver, you will be responsible to drive a small cargo van to/from Long Island and greater NYC area.
            </p>
          </div>

          <Field label="Valid Driver's License?" required testId="field-drivers-license">
            <YesNoCards name="drivers-license" value={data.driversLicense} onChange={(v) => setField("driversLicense", v)} />
          </Field>

          <Field label="Years of driving experience" required testId="field-years-driving">
            <TextField testId="input-years-driving" value={data.yearsDriving} onChange={(v) => setField("yearsDriving", v)} placeholder="e.g. 5" />
          </Field>

          <Field label="Comfortable driving cargo vans?" required hint="Comfortable driving a cargo van in Long Island and NYC." testId="field-cargo-van">
            <YesNoCards name="cargo-van" value={data.cargoVan === "Other" ? "Other" : data.cargoVan} onChange={(v) => setField("cargoVan", v)} options={["Yes", "No", "Other"]} />
            {data.cargoVan === "Other" && (
              <TextField testId="input-cargo-van-other" value={data.cargoVanOther} onChange={(v) => setField("cargoVanOther", v)} placeholder="Tell us more…" />
            )}
          </Field>

          <Field label="Are you able to lift and move 50+ lbs of equipment (e.g., cases, backdrops, gear) with or without reasonable accommodation?" required testId="field-lift-50">
            <YesNoCards name="lift-50" value={data.lift50} onChange={(v) => setField("lift50", v)} />
          </Field>

          <Field label="Experience loading / transporting equipment" required testId="field-load-transport">
            <OptionCards name="load-transport" value={data.loadTransport} onChange={(v) => setField("loadTransport", v)} options={LOAD_TRANSPORT} />
          </Field>
        </div>
      ) : (
        <div className="flex gap-3 rounded-2xl border-2 border-slate-200 bg-slate-50 p-5">
          <Info className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
          <p className="text-sm text-slate-600 leading-relaxed">
            Driving &amp; logistics questions apply to Driver / Staff applicants. You've applied for Photo Booth
            Event Staff, so we've skipped those — you can change your position back in Step 1 anytime.
          </p>
        </div>
      )}

      <Field
        label="This role may require standing for long periods and lifting up to ~ 50 lbs with or without reasonable accommodation."
        required
        testId="field-physical-ack"
      >
        <OptionCards name="physical-ack" value={data.physicalAck} onChange={(v) => setField("physicalAck", v)} options={PHYSICAL_OPTIONS} />
      </Field>

      <Field
        label="CLIQ! Booths maintains a clean, professional appearance standard at all events."
        required
        hint={DRESS_CODE_HINT}
        testId="field-dress-code"
      >
        <OptionCards name="dress-code" value={data.dressCode} onChange={(v) => setField("dressCode", v)} options={DRESS_CODE_OPTIONS} />
      </Field>

      <div className="space-y-7 rounded-2xl border-2 border-dashed border-slate-200 p-5 sm:p-6">
        <p className="text-sm font-semibold text-slate-900">
          Final questions <span className="font-normal text-slate-500">— optional</span>
        </p>
        <p className="text-sm text-slate-500 -mt-4">We're all about creating unforgettable vibes—what makes you want to be part of the team?</p>

        <Field label="Why do you want to work with CLIQ! Booths?" testId="field-why-cliq">
          <TextAreaField testId="input-why-cliq" value={data.whyCliq} onChange={(v) => setField("whyCliq", v)} placeholder="Tell us why…" />
        </Field>

        <Field label="Anything else we should know?" testId="field-anything-else">
          <TextAreaField testId="input-anything-else" value={data.anythingElse} onChange={(v) => setField("anythingElse", v)} placeholder="Anything at all…" />
        </Field>
      </div>
    </div>
  );
}
